/**
 * クレカの引き落とし日の計算(2026-09-04 発注者指示)。
 *
 * クレカのマスタに「締め日」「引き落とし月(翌月/翌々月)」「引き落とし日」を持ち、
 * 購入日からこの購入がいつ引き落とされるかを求める。
 *  - 購入日が締め日以前 → 引き落とし月 = 購入月 + 引き落とし月の差
 *  - 締め日より後      → その翌月
 *  - 日は引き落とし日(31=月末)。月末を超える場合はその月の末日に丸める
 * 土日祝のずれは、カードごとの設定(debit_holiday_shift: 前の平日/後の平日/そのまま。2026-09-08 マスタ管理→支払い方法)に従う。
 */
import { addDays } from './date';
import { weekendOrHoliday } from './jpHolidays';
import type { CashAccount, DateStr, HolidayShift } from './types';

/** その月の末日 */
function lastDayOf(year: number, month1: number): number {
  return new Date(year, month1, 0).getDate();
}

/** クレカに締め日・引き落とし日が設定されているか */
export function hasDebitRule(a: Pick<CashAccount, 'kind' | 'closing_day' | 'debit_day' | 'debit_month_offset'>): boolean {
  return a.kind === 'card' && !!a.closing_day && !!a.debit_day && !!a.debit_month_offset;
}

/** 購入日(YYYY-MM-DD)から引き落とし日を求める。設定がなければ null */
export function computeDebitDate(
  purchaseDate: DateStr,
  a: Pick<CashAccount, 'kind' | 'closing_day' | 'debit_day' | 'debit_month_offset'>,
): DateStr | null {
  if (!hasDebitRule(a) || !/^\d{4}-\d{2}-\d{2}$/.test(purchaseDate)) return null;
  const [y, m, d] = purchaseDate.split('-').map(Number) as [number, number, number];
  const closing = Math.min(a.closing_day!, lastDayOf(y, m));
  // 締め日を過ぎていれば1か月後ろにずれる
  const monthsAhead = (a.debit_month_offset ?? 1) + (d > closing ? 1 : 0);
  const target = new Date(y, m - 1 + monthsAhead, 1);
  const ty = target.getFullYear();
  const tm = target.getMonth() + 1;
  const day = Math.min(a.debit_day!, lastDayOf(ty, tm));
  return `${ty}-${String(tm).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/** 引き落とし日が土日祝のとき、カードの設定どおり前後の平日へずらす(連休でも最大14日まで探す) */
export function shiftDebitForHoliday(date: DateStr, shift: HolidayShift | null | undefined): DateStr {
  if (!shift || shift === 'none') return date;
  let d = date;
  for (let i = 0; i < 14 && weekendOrHoliday(d); i++) d = addDays(d, shift === 'before' ? -1 : 1);
  return d;
}

/** 購入日(利用日)から、土日祝のずらしまで含めた引き落とし日。設定がなければ null */
export function debitDateFor(
  purchaseDate: DateStr,
  a: Pick<CashAccount, 'kind' | 'closing_day' | 'debit_day' | 'debit_month_offset' | 'debit_holiday_shift'>,
): DateStr | null {
  const base = computeDebitDate(purchaseDate, a);
  return base ? shiftDebitForHoliday(base, a.debit_holiday_shift) : null;
}

/** 土日祝の扱いの表示 */
export function debitShiftLabel(shift: HolidayShift | null | undefined): string {
  return shift === 'before' ? '土日祝は前の平日' : shift === 'after' ? '土日祝は後の平日' : '土日祝もそのまま';
}

/** 画面用の説明(例: 「15日締め・翌月10日引き落とし」) */
export function debitRuleLabel(a: Pick<CashAccount, 'closing_day' | 'debit_day' | 'debit_month_offset'>): string {
  if (!a.closing_day || !a.debit_day || !a.debit_month_offset) return '未設定';
  const dayText = (n: number) => (n >= 31 ? '末日' : `${n}日`);
  return `${dayText(a.closing_day)}締め・${a.debit_month_offset === 2 ? '翌々月' : '翌月'}${dayText(a.debit_day)}引き落とし`;
}
